import { SupportedBackgroundColor, SupportedTextColor } from 'src/components/utils/color';

export const EXPIRATION_DAYS = 90;
export const WARNING_DAYS_BEFORE_EXPIRATION = 14;

export type ExpirationStatus = 'none' | 'warning' | 'expired';

export interface ExpirationInfo {
  status: ExpirationStatus;
  daysRemaining: number;
}

export interface BannerConfig {
  backgroundColor: SupportedBackgroundColor;
  color: SupportedTextColor;
}

export function getExpirationInfo(updatedAt: string): ExpirationInfo {
  const now = new Date();
  const lastUpdated = new Date(updatedAt);
  const daysSinceUpdate = Math.floor((now.getTime() - lastUpdated.getTime()) / (1000 * 60 * 60 * 24));
  const daysRemaining = EXPIRATION_DAYS - daysSinceUpdate;

  if (daysRemaining <= 0) {
    return { status: 'expired', daysRemaining };
  }

  if (daysRemaining <= WARNING_DAYS_BEFORE_EXPIRATION) {
    return { status: 'warning', daysRemaining };
  }

  return { status: 'none', daysRemaining };
}

export function getBannerConfig(status: ExpirationStatus): BannerConfig | null {
  switch (status) {
    case 'expired':
      return { backgroundColor: 'error', color: 'foreground' };
    case 'warning':
      return { backgroundColor: 'warning', color: 'high-contrast-grey' };
    default:
      return null;
  }
}

export function generateServiceUserMessage(status: ExpirationStatus, daysRemaining: number): string {
  if (status === 'expired') {
    return 'Microsoft Service User integration has expired. Reconnect immediately to restore functionality. Be sure to reconnect using the service account email.';
  }
  return `Microsoft Service User integration expires in ${daysRemaining} days. Please reconnect to avoid interruption. Be sure to reconnect using the service account email.`;
}

export function generateCustomIntegrationMessage(
  status: ExpirationStatus,
  daysRemaining: number,
  emails: string[]
): string {
  const emailList = emails.join(', ');

  if (status === 'expired') {
    return `Microsoft Custom integration for ${emailList} has expired. Reconnect immediately to restore functionality. Login to ModernLoop using the shared email addresses to reconnect each employee.`;
  }
  return `Microsoft Custom integration for ${emailList} expires in ${daysRemaining} days. Please reconnect to avoid interruption. Login to ModernLoop using the shared email addresses to reconnect each employee.`;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TranslateFunction = (key: string, options?: any) => string;

export function generateServiceUserMessageI18n(
  t: TranslateFunction,
  status: ExpirationStatus,
  daysRemaining: number
): string {
  if (status === 'expired') {
    return t('hiring-dashboard-nav:nav.banner.msServiceUserExpired');
  }
  return t('hiring-dashboard-nav:nav.banner.msServiceUserExpiring', { daysRemaining });
}

export function generateCustomIntegrationMessageI18n(
  t: TranslateFunction,
  status: ExpirationStatus,
  daysRemaining: number,
  emails: string[]
): string {
  const emailList = emails.join(', ');

  if (status === 'expired') {
    return t('hiring-dashboard-nav:nav.banner.msCustomExpired', { emailList });
  }
  return t('hiring-dashboard-nav:nav.banner.msCustomExpiring', { emailList, daysRemaining });
}
